// src/components/PartnerAssignField.jsx
// Wiederverwendbares Feld, um Firmen/Partner:innen/Auftraggeber:innen einem
// Projekt zuzuordnen — nach demselben Prinzip wie die Verantwortlichen-Auswahl
// in den Pendenzen: bereits zugeordnete werden als Chips angezeigt, die Suche
// findet zusätzlich bereits im CRM vorhandene (aber noch nicht zugeordnete)
// Firmen, und was nicht gefunden wird, kann direkt als neue Firma im CRM
// erfasst werden (siehe PendenzResponsibleCreateModal/-LinkModal).
import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { getAllPartners } from "../services/azurePartnersService";
import {
  getProjectRelations,
  createRelation,
  deleteRelation,
} from "../services/azureRelationsService";
import PendenzResponsibleCreateModal from "./PendenzResponsibleCreateModal";
import PendenzResponsibleLinkModal from "./PendenzResponsibleLinkModal";
import "../styles/pendenzen.scss";
import "../styles/_partner-assign-field.scss";

const PartnerAssignField = ({
  projectCode,
  // Feste Rolle (RELATION_TYPES.AUFTRAGGEBERIN/.PARTNER/...), wenn roleOptions
  // nicht gesetzt ist.
  relationType,
  // Optional: [{value, label}] – zeigt vor dem Suchfeld eine Rollen-Auswahl,
  // damit z.B. im Controlling zwischen Auftraggeberin/Partner/Consultant/
  // Contractor gewählt werden kann. Ohne roleOptions gilt immer relationType.
  roleOptions = null,
  // Optional: [{id, name}] – aktiviert pro Firma eine Phasen-Zuordnung
  // (Mehrfachauswahl). Keine Auswahl = Firma gilt für alle Phasen.
  phases = null,
  label,
  placeholder = "Firma suchen oder neu erfassen…",
  disabled = false,
  emptyHint = "Noch keine Firma zugeordnet.",
  onChanged,
}) => {
  const [relations, setRelations] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(
    roleOptions?.[0]?.value || relationType
  );
  const [createModal, setCreateModal] = useState(null);
  const [linkModal, setLinkModal] = useState(null);

  // Dropdown fixed positionieren (wie ResponsibleSelect in Pendenzen.jsx),
  // damit es nicht von einem ueberlaufenden/scrollenden Container (z.B. das
  // "Ansätze"/"Firmen"-Modal mit overflow: hidden bzw. overflow-y: auto)
  // abgeschnitten oder mit eingescrollt wird.
  const wrapRef = useRef(null);
  const [dropPos, setDropPos] = useState(null);
  useLayoutEffect(() => {
    if (!open) {
      setDropPos(null);
      return undefined;
    }
    const measure = () => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setDropPos({ top: r.bottom + 2, left: r.left, width: r.width });
    };
    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [open]);

  const acceptedRoles = useMemo(
    () => (roleOptions ? roleOptions.map((r) => r.value) : [relationType]),
    [roleOptions, relationType]
  );

  const load = useCallback(async () => {
    if (!projectCode) return;
    setLoading(true);
    setError(null);
    try {
      const [rels, allPartners] = await Promise.all([
        getProjectRelations(projectCode),
        getAllPartners(),
      ]);
      setRelations(
        rels.filter(
          (r) => r.targetType === "partner" && acceptedRoles.includes(r.relationType)
        )
      );
      setPartners(allPartners);
    } catch (err) {
      setError(err?.message || "Firmen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [projectCode, acceptedRoles]);

  useEffect(() => {
    load();
  }, [load]);

  const assignedIds = useMemo(
    () => new Set(relations.map((r) => r.targetId)),
    [relations]
  );

  const partnerName = useCallback(
    (id) => partners.find((p) => p.id === id)?.name || "Unbekannt",
    [partners]
  );

  const roleLabel = useCallback(
    (value) => roleOptions?.find((r) => r.value === value)?.label || value,
    [roleOptions]
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return partners
      .filter((p) => !assignedIds.has(p.id))
      .filter((p) => p.name.toLowerCase().includes(q))
      .slice(0, 20);
  }, [partners, assignedIds, query]);

  const exactMatch = useMemo(
    () =>
      partners.some(
        (p) => p.name.trim().toLowerCase() === query.trim().toLowerCase()
      ),
    [partners, query]
  );

  const currentRole = () => (roleOptions ? selectedRole : relationType);

  const handlePickExisting = (partner) => {
    setOpen(false);
    setQuery("");
    setLinkModal({
      option: { id: `org:${partner.id}`, label: partner.name, group: "Organisation" },
    });
  };

  const handleCreateNew = () => {
    setOpen(false);
    setCreateModal({ initialName: query.trim() });
  };

  const handleRemove = async (relation) => {
    if (
      !window.confirm(`"${partnerName(relation.targetId)}" nicht mehr zuordnen?`)
    )
      return;
    try {
      await deleteRelation(relation.id);
      await load();
      onChanged?.();
    } catch (err) {
      setError(err?.message || "Konnte nicht entfernt werden.");
    }
  };

  // Relations koennen nicht upgedated werden (Azure Table erzeugt bei jedem
  // createRelation() einen neuen RowKey) - Phasen-Aenderung daher als
  // Loeschen + Neu-Anlegen mit gleichem Ziel/gleicher Rolle.
  const handlePhasesChange = async (relation, selectedPhaseIds) => {
    try {
      await deleteRelation(relation.id);
      await createRelation({
        sourceType: "project",
        sourceId: projectCode,
        targetType: "partner",
        targetId: relation.targetId,
        relationType: relation.relationType,
        function: selectedPhaseIds.join(","),
        description: relation.description,
        visibility: relation.visibility,
      });
      await load();
      onChanged?.();
    } catch (err) {
      setError(err?.message || "Phasen konnten nicht gespeichert werden.");
    }
  };

  if (!projectCode) return null;

  return (
    <div className="partner-assign-field">
      {label && <label>{label}</label>}
      {error && (
        <div
          className="pendenzen__error"
          role="alert"
          onClick={() => setError(null)}
        >
          {error}
        </div>
      )}

      <div className="partner-assign-field__chips">
        {relations.length === 0 && !loading && (
          <span className="partner-assign-field__empty">{emptyHint}</span>
        )}
        {relations.map((r) => {
          const selectedPhaseIds = r.function ? r.function.split(",").filter(Boolean) : [];
          return (
            <span key={r.id} className="partner-assign-field__chip">
              <span className="partner-assign-field__chip-name">
                {partnerName(r.targetId)}
              </span>
              {roleOptions && (
                <em className="partner-assign-field__chip-role">
                  {roleLabel(r.relationType)}
                </em>
              )}
              {phases && phases.length > 0 && (
                <select
                  multiple
                  size={1}
                  className="partner-assign-field__chip-phases"
                  value={selectedPhaseIds}
                  disabled={disabled}
                  title="Phasen einschränken (keine Auswahl = alle Phasen)"
                  onChange={(e) =>
                    handlePhasesChange(
                      r,
                      Array.from(e.target.selectedOptions, (o) => o.value)
                    )
                  }
                >
                  {phases.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              )}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(r)}
                  aria-label={`${partnerName(r.targetId)} entfernen`}
                >
                  ×
                </button>
              )}
            </span>
          );
        })}
      </div>

      {!disabled && (
        <div className="partner-assign-field__input-row">
          {roleOptions && (
            <select
              className="partner-assign-field__role-select"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              {roleOptions.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          )}
          <div className="partner-assign-field__input-wrap" ref={wrapRef}>
            <input
              type="text"
              value={query}
              placeholder={placeholder}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 150)}
            />
            {open && query.trim() && (
              <ul
                className="partner-assign-field__dropdown"
                style={
                  dropPos
                    ? {
                        position: "fixed",
                        top: dropPos.top,
                        left: dropPos.left,
                        width: dropPos.width,
                        right: "auto",
                      }
                    : undefined
                }
              >
                {matches.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handlePickExisting(p);
                      }}
                    >
                      <span>{p.name}</span>
                      <span className="partner-assign-field__dropdown-hint">
                        Im CRM
                      </span>
                    </button>
                  </li>
                ))}
                {!exactMatch && (
                  <li>
                    <button
                      type="button"
                      className="partner-assign-field__dropdown-create"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleCreateNew();
                      }}
                    >
                      ＋ „{query.trim()}" als neue Firma erfassen
                    </button>
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* CRM-Modale per Portal an den body: Das Feld sitzt u.a. im <form> des
          ProjectForm — ein verschachteltes <form> ist ungueltiges DOM, und die
          Nachfahren-Styles des umgebenden Formulars wuerden die Modal-Felder
          verzerren (lessons-learned 2026-09-07). Der Wrapper hebt den z-index
          ueber umgebende Modale (Controlling "Firmen") und den Vollbild-Editor. */}
      {linkModal &&
        createPortal(
          <div className="crm-modal-portal">
            <PendenzResponsibleLinkModal
              option={linkModal.option}
              projectCode={projectCode}
              relationType={currentRole()}
              onClose={() => setLinkModal(null)}
              onLinked={async () => {
                setLinkModal(null);
                await load();
                onChanged?.();
              }}
            />
          </div>,
          document.body
        )}
      {createModal &&
        createPortal(
          <div className="crm-modal-portal">
            <PendenzResponsibleCreateModal
              type="organization"
              initialName={createModal.initialName}
              partners={partners}
              projectCode={projectCode}
              relationType={currentRole()}
              onCreated={async () => {
                setCreateModal(null);
                await load();
                onChanged?.();
              }}
              onClose={() => setCreateModal(null)}
            />
          </div>,
          document.body
        )}
    </div>
  );
};

export default PartnerAssignField;
